import { ChangeEvent } from 'react';
import { ErrorMessage, Field, useField } from "formik";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import cx from "classnames";

interface SelectOption {
    value: string;
    label: string;
}

/**
 * A Formik-integrated select dropdown with optional floating label,
 * placeholder, required indicator, and helper text.
 */
export default function Select({ options, name, label, placeholder = '', onChange, required = false, disabled = false, helperText }: {
    /** The available dropdown options. */
    options: SelectOption[];
    /** Formik field name. */
    name: string;
    /** Optional floating label text. When provided, wraps the field in a FloatingLabel. */
    label?: string;
    /** Optional placeholder option shown when no value is selected. */
    placeholder?: string;
    /** Optional change handler called after the new value is set. */
    onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
    /** Marks the field as required with a visual indicator. */
    required?: boolean;
    /** Disables the select input. */
    disabled?: boolean;
    /** Helper text displayed below the field. */
    helperText?: string;
}) {
    const [{ value },, { setValue }] = useField(name);
    const field = (
        <Field
            aria-label={label}
            as="select"
            name={name}
            className="form-control form-select"
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                e.persist();
                if (onChange) {
                    onChange(e);
                }
                setValue(e.target.value);
            }}
            disabled={disabled}
            value={value}
        >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((o) => (
                <option value={o.value} key={o.value}>{o.label}</option>
            ))}
        </Field>
    );
    return (
        <>
            {label !== undefined ? (
                <FloatingLabel controlId={label} label={(
                    <div className={cx({ "required-field": required })}>
                        {label}
                    </div>
                )}>
                    {field}
                </FloatingLabel>
            ) : field}
            {helperText && <div className="text-right text-sm gray-500 mb-3">{helperText}</div>}
            <ErrorMessage name={name} className="text-danger px-1" component="span" />
        </>
    );
}
